import { Extension, type Editor } from '@tiptap/react';
import { Plugin, PluginKey } from 'prosemirror-state';
import { marked } from 'marked';
import { api } from '@/lib/api';

/**
 * Smart paste cascade:
 *  1) Single embeddable URL (YouTube/Vimeo/Loom/Twitter/X) → iframe embed.
 *  2) Single plain URL → minimal bookmark card (anchor).
 *  3) Text that looks like Markdown (#, ##, -, *, ```) → parsed HTML blocks.
 *  4) Otherwise → default paste behaviour.
 *
 * Also handles:
 *  - Images dropped or pasted from the clipboard: auto-upload to /api/v1/media
 *    and insert as <img>.
 */

interface SmartPasteOptions {
  /** Show toast/log on errors. Defaults to console.warn. */
  onError?: (message: string, err?: unknown) => void;
}

export const SmartPaste = Extension.create<SmartPasteOptions>({
  name: 'smartPaste',

  addOptions() {
    return {
      onError: (msg, err) => console.warn('[SmartPaste]', msg, err),
    };
  },

  addProseMirrorPlugins() {
    const opts = this.options;
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('smartPaste'),
        props: {
          handlePaste(_view, event) {
            const clipboard = event.clipboardData;
            if (!clipboard) return false;

            // 1) If there's an image in the clipboard, upload and insert.
            const imageFile = findImageFile(clipboard);
            if (imageFile) {
              event.preventDefault();
              void uploadAndInsertImage(editor, imageFile, opts);
              return true;
            }

            const text = clipboard.getData('text/plain');
            if (!text) return false;
            const trimmed = text.trim();

            // 2) Single URL?
            if (isSingleUrl(trimmed)) {
              const embedHtml = buildEmbedHtml(trimmed);
              if (embedHtml) {
                event.preventDefault();
                editor.chain().focus().insertContent(embedHtml).run();
                return true;
              }
              // Plain URL → bookmark anchor
              event.preventDefault();
              const safe = escapeHtml(trimmed);
              editor
                .chain()
                .focus()
                .insertContent(
                  `<p><a href="${safe}" target="_blank" rel="noopener noreferrer">${safe}</a></p>`,
                )
                .run();
              return true;
            }

            // 3) Looks like Markdown?
            if (looksLikeMarkdown(trimmed)) {
              try {
                const html = marked.parse(trimmed, { breaks: true, async: false }) as string;
                event.preventDefault();
                editor.chain().focus().insertContent(html).run();
                return true;
              } catch (err) {
                opts.onError?.('No se pudo parsear como Markdown', err);
              }
            }

            return false;
          },

          handleDrop(_view, event) {
            const dragEvent = event as DragEvent;
            const dt = dragEvent.dataTransfer;
            if (!dt || !dt.files || dt.files.length === 0) return false;
            const imageFile = Array.from(dt.files).find((f) => f.type.startsWith('image/'));
            if (!imageFile) return false;
            event.preventDefault();
            void uploadAndInsertImage(editor, imageFile, opts);
            return true;
          },
        },
      }),
    ];
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function findImageFile(clipboard: DataTransfer): File | null {
  for (const item of Array.from(clipboard.items ?? [])) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const f = item.getAsFile();
      if (f) return f;
    }
  }
  for (const f of Array.from(clipboard.files ?? [])) {
    if (f.type.startsWith('image/')) return f;
  }
  return null;
}

async function uploadAndInsertImage(
  editor: Editor,
  file: File,
  opts: SmartPasteOptions,
): Promise<void> {
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post<{ success: boolean; data: { url: string } }>(
      '/media',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    const url = res.data?.data?.url;
    if (!url) {
      opts.onError?.('Subida sin URL');
      return;
    }
    editor.chain().focus().setImage({ src: url }).run();
  } catch (err) {
    opts.onError?.('No se pudo subir la imagen', err);
  }
}

function isSingleUrl(text: string): boolean {
  if (text.includes('\n')) return false;
  if (text.includes(' ')) return false;
  try {
    const u = new URL(text);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function looksLikeMarkdown(text: string): boolean {
  // ATX headings, lists, fenced code, blockquotes, bold/italic markers
  return (
    /^#{1,6}\s/m.test(text) ||
    /^[-*+]\s/m.test(text) ||
    /^\d+\.\s/m.test(text) ||
    /^>\s/m.test(text) ||
    /^```[\s\S]*?```$/m.test(text) ||
    /\*\*[^\s*][^*]*\*\*/.test(text) ||
    /\[[^\]]+]\([^)]+\)/.test(text)
  );
}

function buildEmbedHtml(url: string): string | null {
  const yt = matchYouTube(url);
  if (yt) {
    return iframe(
      `https://www.youtube.com/embed/${yt}`,
      'YouTube',
    );
  }
  const vm = matchVimeo(url);
  if (vm) {
    return iframe(`https://player.vimeo.com/video/${vm}`, 'Vimeo');
  }
  const loom = matchLoom(url);
  if (loom) {
    return iframe(`https://www.loom.com/embed/${loom}`, 'Loom');
  }
  const tweet = matchTwitter(url);
  if (tweet) {
    // Simple embed via publish.twitter.com is not always reliable from CSP;
    // fall back to a quote-style anchor that the front renders nicely.
    const safe = escapeHtml(url);
    return `<blockquote class="twitter-tweet"><p><a href="${safe}" target="_blank" rel="noopener noreferrer">${safe}</a></p></blockquote>`;
  }
  return null;
}

function iframe(src: string, title: string): string {
  return `<div class="embed-wrap" data-embed="${title.toLowerCase()}"><iframe src="${src}" title="${escapeHtml(title)}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="lazy"></iframe></div>`;
}

function matchYouTube(url: string): string | null {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i);
  return m?.[1] ?? null;
}

function matchVimeo(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d{5,})/i);
  return m?.[1] ?? null;
}

function matchLoom(url: string): string | null {
  const m = url.match(/loom\.com\/share\/([\w-]{8,})/i);
  return m?.[1] ?? null;
}

function matchTwitter(url: string): boolean {
  return /^(https?:\/\/)?(www\.)?(twitter|x)\.com\/[^/]+\/status\/\d+/i.test(url);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
